import type { AiSettings } from '../types';
import { LANGUAGES } from '../types';

interface Props {
  form: AiSettings;
  saved: boolean;
  saving: boolean;
  saveError?: string | null;
  onSave: () => void;
}

export default function AiStatusSidebar({ form, saved, saving, saveError, onSave }: Props) {
  const langLabel = LANGUAGES.find((l) => l.value === form.language)?.label ?? form.language;

  const readiness = [
    { label: 'Enabled', done: form.assistant_enabled },
    { label: 'Name set', done: !!form.assistant_name.trim() },
    { label: 'Business context', done: form.business_context.length >= 50 },
    { label: 'Services context', done: form.services_context.length >= 30 },
    { label: 'Lead capture', done: true },
    { label: 'Tone & language', done: !!form.tone && !!form.language },
  ];
  const doneCount = readiness.filter((r) => r.done).length;
  const pct = Math.round((doneCount / readiness.length) * 100);

  return (
    <div className="space-y-4">
      {/* Quick status */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="text-slate-500 text-xs uppercase tracking-wider mb-4">Live Status</h3>
        <div className="space-y-3">
          {[
            {
              label: 'Assistant',
              value: form.assistant_enabled ? 'Enabled' : 'Disabled',
              color: form.assistant_enabled ? 'text-emerald-500' : 'text-slate-400',
            },
            { label: 'Name', value: form.assistant_name || '—', color: 'text-[#0f172a]' },
            {
              label: 'Tone',
              value: form.tone.charAt(0).toUpperCase() + form.tone.slice(1),
              color: 'text-[#0f172a]',
            },
            { label: 'Language', value: langLabel, color: 'text-[#0f172a]' },
            {
              label: 'Lead Capture',
              value: form.lead_capture_enabled ? 'On' : 'Off',
              color: form.lead_capture_enabled ? 'text-emerald-500' : 'text-slate-400',
            },
            {
              label: 'Book Goal',
              value: form.appointment_goal_enabled ? 'Active' : 'Off',
              color: form.appointment_goal_enabled ? 'text-emerald-500' : 'text-slate-400',
            },
            {
              label: 'WA Handoff',
              value: form.whatsapp_handoff_enabled ? 'Active' : 'Off',
              color: form.whatsapp_handoff_enabled ? 'text-emerald-500' : 'text-slate-400',
            },
          ].map((item) => (
            <div key={item.label} className="flex items-center justify-between">
              <span className="text-slate-400 text-xs">{item.label}</span>
              <span className={`text-xs font-medium ${item.color}`}>{item.value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Readiness meter */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-slate-500 text-xs uppercase tracking-wider">Readiness</h3>
          <span
            className={`text-xs font-bold ${pct === 100 ? 'text-emerald-500' : 'text-[#C8A96E]'}`}
          >
            {pct}%
          </span>
        </div>
        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mb-4">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{
              width: `${pct}%`,
              backgroundColor: pct === 100 ? '#10b981' : '#C8A96E',
            }}
          />
        </div>
        <div className="space-y-1.5">
          {readiness.map((item) => (
            <div key={item.label} className="flex items-center gap-2">
              <div
                className={`w-4 h-4 flex items-center justify-center rounded-full shrink-0 ${
                  item.done ? 'bg-emerald-50 border border-emerald-200' : 'bg-slate-50 border border-slate-200'
                }`}
              >
                <i
                  className={`text-[9px] ${
                    item.done ? 'ri-check-line text-emerald-500' : 'ri-subtract-line text-slate-300'
                  }`}
                />
              </div>
              <span className={`text-[11px] ${item.done ? 'text-slate-600' : 'text-slate-400'}`}>
                {item.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Save button */}
      <button
        type="button"
        onClick={onSave}
        disabled={saving}
        className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
        style={{ backgroundColor: '#C8A96E', color: '#fff' }}
      >
        {saving ? (
          <>
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            Saving…
          </>
        ) : (
          <>
            <i className="ri-save-line text-sm" />
            Save Settings
          </>
        )}
      </button>

      {saved && !saveError && (
        <div className="flex items-center justify-center gap-1.5 text-emerald-500 text-xs">
          <i className="ri-check-double-line" />
          Saved successfully
        </div>
      )}
      {saveError && (
        <div className="flex items-start gap-1.5 text-red-500 text-xs bg-red-50 rounded-lg px-3 py-2.5 border border-red-100">
          <i className="ri-error-warning-line shrink-0 mt-0.5" />
          <span>{saveError}</span>
        </div>
      )}

      {/* Notice */}
      <div className="px-4 py-3.5 bg-slate-50 rounded-xl border border-slate-200">
        <p className="text-slate-400 text-[11px] leading-relaxed">
          <i className="ri-shield-keyhole-line mr-1" />
          AI model keys (OpenAI, Anthropic) must be stored as{' '}
          <span className="font-mono font-semibold text-slate-500">Edge Function secrets</span>.
          This panel configures behavior only.
        </p>
      </div>
    </div>
  );
}